// src/services/avatarService.js
//
// AVATARS (step 4) — the browser side of the avatar maker.
//
//   useMyAvatar()        live { look, avatarUrl, loaded } for the signed-in
//                        student. `look` is the avatar-maker look
//                        (avatarParts.js) or null if they haven't made one
//                        yet — then the old picture (avatarUrl) still shows.
//   saveAvatarLook(look) saves through the saveAvatarLook Cloud Function,
//                        which checks every part and also copies the look
//                        to avatarLooks/{uid} so classmates can see it.
//   useAvatarLooks(uids) { uid: { look, outfit } } for a list of students
//                        (leaderboards). Read once per uid and cached.

import { useEffect, useState } from "react";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebaseConfig";
import { cleanLook } from "../components/avatar/avatarParts";

export const useMyAvatar = () => {
  const [state, setState] = useState({ look: null, avatarUrl: "", loaded: false });
  useEffect(() => {
    let stopDoc = () => {};
    const stopAuth = onAuthStateChanged(getAuth(), (user) => {
      stopDoc();
      if (!user) {
        setState({ look: null, avatarUrl: "", loaded: true });
        return;
      }
      stopDoc = onSnapshot(
        doc(db, "users", user.uid),
        (s) => {
          const d = (s.exists() && s.data()) || {};
          setState({ look: d.avatarLook ? cleanLook(d.avatarLook) : null, avatarUrl: d.avatarUrl || d.avatar || "", loaded: true });
        },
        () => setState((p) => ({ ...p, loaded: true }))
      );
    });
    return () => {
      stopDoc();
      stopAuth();
    };
  }, []);
  return state;
};

export const saveAvatarLook = async (look) => {
  const res = await httpsCallable(functions, "saveAvatarLook")({ look: cleanLook(look) });
  return res?.data || null;
};

const lookCache = new Map(); // uid -> Promise<{look, outfit}>

const fetchLook = (uid) => {
  if (!lookCache.has(uid)) {
    lookCache.set(
      uid,
      getDoc(doc(db, "avatarLooks", uid))
        .then((s) => {
          const d = (s.exists() && s.data()) || {};
          return { look: d.look ? cleanLook(d.look) : null, outfit: d.outfit || {}, avatar: d.avatar || "" };
        })
        .catch(() => ({ look: null, outfit: {}, avatar: "" }))
    );
  }
  return lookCache.get(uid);
};

export const useAvatarLooks = (uids) => {
  const key = (uids || []).slice().sort().join(",");
  const [map, setMap] = useState({});
  useEffect(() => {
    let alive = true;
    const list = key ? key.split(",") : [];
    Promise.all(list.map((u) => fetchLook(u).then((v) => [u, v]))).then((pairs) => {
      if (alive) setMap(Object.fromEntries(pairs));
    });
    return () => {
      alive = false;
    };
  }, [key]);
  return map;
};

/** Forget a cached look (after the student saves a new one). */
export const forgetLook = (uid) => lookCache.delete(uid);
